<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ChevronDown, Check } from 'lucide-vue-next'
import { useLocaleStore } from '../stores/locale'

const localeStore = useLocaleStore()
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const currentFlag = computed(() =>
  localeStore.currentLocaleConfig?.flag || '🌐'
)

const currentName = computed(() =>
  localeStore.currentLocaleConfig?.name || 'Language'
)

function selectLocale(code: string) {
  localeStore.setUserLocale(code)
  isOpen.value = false
}

function toggleDropdown() {
  isOpen.value = !isOpen.value
}

// Close dropdown when clicking outside
function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      @click="toggleDropdown"
      class="flex items-center gap-1.5 p-1.5 rounded-lg bg-gray-100 dark:bg-dark-elevated hover:bg-gray-200 dark:hover:bg-dark-border transition-colors"
      :title="currentName"
    >
      <span class="text-base">{{ currentFlag }}</span>
      <ChevronDown
        class="w-3 h-3 text-gray-400 transition-transform"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Dropdown -->
    <Transition name="dropdown">
      <div
        v-if="isOpen"
        class="absolute right-0 mt-2 w-56 max-h-80 overflow-y-auto bg-white dark:bg-dark-elevated rounded-xl shadow-lg border border-gray-100 dark:border-dark-border z-50"
      >
        <div class="py-1">
          <button
            v-for="lang in localeStore.availableLocales"
            :key="lang.code"
            @click="selectLocale(lang.code)"
            class="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-border transition-colors"
            :class="{ 'bg-blue-50 dark:bg-ios-blue/10': localeStore.locale === lang.code }"
          >
            <span class="text-lg">{{ lang.flag }}</span>
            <div class="flex-1 min-w-0">
              <span class="block text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{{ lang.name }}</span>
              <span class="block text-xs text-gray-500 dark:text-gray-400 truncate">{{ lang.englishName }}</span>
            </div>
            <Check
              v-if="localeStore.locale === lang.code"
              class="w-4 h-4 text-blue-600 dark:text-ios-blue flex-shrink-0"
            />
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Custom scrollbar for the dropdown */
.max-h-80::-webkit-scrollbar {
  width: 6px;
}

.max-h-80::-webkit-scrollbar-track {
  background: transparent;
}

.max-h-80::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.dark .max-h-80::-webkit-scrollbar-thumb {
  background-color: rgba(75, 85, 99, 0.5);
}
</style>
